import { EChallenge } from '@/redux/constants/challenge';
import instance from '@/redux/middlewares/api';
import { RootState } from '@/redux/store';
import {
  IChallengeCompanyParam,
  ICompanyChallenge,
  IUpdateCompanyChallengeParams,
  IUpdateUserChallengeStepParams,
  IUpdateUserTeamNameParams,
  IUserChallenge,
  IUserChallengeUpdateResponse,
} from '@/types/challenges';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getChallengesUrlByRoleName,
  getUserChallengesUrlByRoleName,
} from '@/utils/userRoleHelpers';

const getUserChallenge = createAsyncThunk(
  EChallenge.getUserChallenge,
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getUserChallengesUrlByRoleName(role);

      const response = await instance.get<IUserChallenge>(`${url}/challenges`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const updateUserTeamName = createAsyncThunk(
  EChallenge.updateUserTeamName,
  async (
    { companiesChallengesId, teamName }: IUpdateUserTeamNameParams,
    { getState, rejectWithValue }
  ) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getUserChallengesUrlByRoleName(role);

      const response = await instance.patch<IUserChallengeUpdateResponse>(
        `${url}/challenges/${companiesChallengesId}`,
        { teamName }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const updateUserChallengeStep = createAsyncThunk(
  EChallenge.updateUserChallengeStep,
  async (
    { companiesChallengesId, index, value }: IUpdateUserChallengeStepParams,
    { getState, rejectWithValue }
  ) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getUserChallengesUrlByRoleName(role);

      const response = await instance.patch<IUserChallengeUpdateResponse>(
        `${url}/challenges/${companiesChallengesId}/values`,
        { index, value }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const getCompanyChallenge = createAsyncThunk(
  EChallenge.getCompanyChallenge,
  async (
    { companyId }: IChallengeCompanyParam,
    { getState, rejectWithValue }
  ) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getChallengesUrlByRoleName(role);

      const response = await instance.get<ICompanyChallenge>(
        `${url}/${companyId}/challenge`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const updateCompanyChallenge = createAsyncThunk(
  EChallenge.updateCompanyChallenge,
  async (
    { companyId, ...data }: IUpdateCompanyChallengeParams,
    { getState, rejectWithValue }
  ) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getChallengesUrlByRoleName(role);

      const response = await instance.patch<ICompanyChallenge>(
        `${url}/${companyId}/challenge`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const resetCompanyChallenge = createAsyncThunk(
  EChallenge.resetCompanyChallenge,
  async (
    { companyId }: IChallengeCompanyParam,
    { getState, rejectWithValue }
  ) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getChallengesUrlByRoleName(role);

      await instance.delete(`${url}/${companyId}/challenge`);
      return { companyId };
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

const loadChallengesReport = createAsyncThunk(
  EChallenge.loadChallengesReport,
  async (companyId: number, { getState, rejectWithValue }) => {
    try {
      const { user } = getState() as RootState;
      const role = user.user?.role.name;
      const url = getChallengesUrlByRoleName(role);

      const response = await instance.get(
        `${url}/${companyId}/challenge/download`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ error: error.message });
    }
  }
);

export {
  getUserChallenge,
  updateUserTeamName,
  updateUserChallengeStep,
  getCompanyChallenge,
  updateCompanyChallenge,
  resetCompanyChallenge,
  loadChallengesReport,
};
